import React from 'react';
import { theme, alpha } from '../../../theme';
import { FixedBooking, DAY_OF_WEEK_LABELS, BOOKING_COLORS } from '../../../types/venue';
import { Clock, User, Phone, DollarSign, Calendar, Play, Pause, Trash2, Edit2 } from 'lucide-react';

interface FixedBookingCardProps {
  fixedBooking: FixedBooking;
  onEdit?: (fixedBooking: FixedBooking) => void;
  onPause?: (id: string) => void;
  onResume?: (id: string) => void;
  onCancel?: (id: string) => void;
  loading?: boolean;
}

const FixedBookingCard: React.FC<FixedBookingCardProps> = ({
  fixedBooking,
  onEdit,
  onPause,
  onResume,
  onCancel,
  loading = false,
}) => {
  const getStatusStyle = () => {
    switch (fixedBooking.status) {
      case 'ACTIVE':
        return {
          backgroundColor: `${alpha('success', '20')}`,
          color: theme.colors.success,
          label: 'Activo',
        };
      case 'PAUSED':
        return {
          backgroundColor: `${alpha('warning', '20')}`,
          color: theme.colors.warning,
          label: 'Pausado',
        };
      case 'CANCELLED':
        return {
          backgroundColor: `${alpha('danger', '20')}`,
          color: theme.colors.danger,
          label: 'Cancelado',
        };
      default:
        return {
          backgroundColor: `${alpha('textSecondary', '20')}`,
          color: theme.colors.textSecondary,
          label: fixedBooking.status,
        };
    }
  };

  const statusStyle = getStatusStyle();

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `1px solid ${theme.colors.border}`,
    overflow: 'hidden',
    transition: 'all 0.2s',
    opacity: loading ? 0.7 : 1,
    pointerEvents: loading ? 'none' : 'auto',
  };

  const headerStyle: React.CSSProperties = {
    backgroundColor: `${BOOKING_COLORS.FIXED}15`,
    borderBottom: `1px solid ${theme.colors.border}`,
    padding: '16px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const dayBadgeStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  };

  const dayLabelStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSubtitle,
    fontWeight: theme.fonts.weightBold,
    color: BOOKING_COLORS.FIXED,
  };

  const statusBadgeStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: theme.fonts.weightBold,
    ...statusStyle,
  };

  const contentStyle: React.CSSProperties = {
    padding: '20px',
  };

  const infoRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  };

  const iconContainerStyle: React.CSSProperties = {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    backgroundColor: `${alpha('primary', '15')}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  const infoTextStyle: React.CSSProperties = {
    flex: 1,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    color: theme.colors.textSecondary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '2px',
  };

  const valueStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.text,
    fontWeight: theme.fonts.weightBold,
  };

  const footerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    padding: '12px 20px',
    borderTop: `1px solid ${theme.colors.border}`,
    backgroundColor: theme.colors.background,
  };

  const buttonStyle: React.CSSProperties = {
    padding: '8px 12px',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeSmall,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    border: 'none',
  };

  const primaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: theme.colors.primary,
    color: '#000',
  };

  const secondaryButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: 'transparent',
    border: `1px solid ${theme.colors.border}`,
    color: theme.colors.text,
  };

  const dangerButtonStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: `${alpha('danger', '20')}`,
    color: theme.colors.danger,
    border: `1px solid ${theme.colors.danger}`,
  };

  const formatDate = (dateStr: string) => {
    const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const date = new Date(datePart + 'T00:00:00');
    return date.toLocaleDateString('es-AR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <div style={dayBadgeStyle}>
          <span style={{ fontSize: '24px' }}>🔄</span>
          <span style={dayLabelStyle}>
            {DAY_OF_WEEK_LABELS[fixedBooking.dayOfWeek]}
          </span>
        </div>
        <span style={statusBadgeStyle}>{statusStyle.label}</span>
      </div>

      <div style={contentStyle}>
        {/* Venue */}
        {fixedBooking.venue && (
          <div style={infoRowStyle}>
            <div style={iconContainerStyle}>
              <Calendar size={16} color={theme.colors.primary} />
            </div>
            <div style={infoTextStyle}>
              <div style={labelStyle}>Cancha</div>
              <div style={valueStyle}>
                {fixedBooking.venue.name} ({fixedBooking.venue.venueType.replace('FUTBOL_', 'F')})
              </div>
            </div>
          </div>
        )}

        {/* Time */}
        <div style={infoRowStyle}>
          <div style={iconContainerStyle}>
            <Clock size={16} color={theme.colors.primary} />
          </div>
          <div style={infoTextStyle}>
            <div style={labelStyle}>Horario</div>
            <div style={valueStyle}>
              {fixedBooking.startTime} - {fixedBooking.endTime}
            </div>
          </div>
        </div>

        {/* Customer */}
        <div style={infoRowStyle}>
          <div style={iconContainerStyle}>
            <User size={16} color={theme.colors.primary} />
          </div>
          <div style={infoTextStyle}>
            <div style={labelStyle}>Cliente</div>
            <div style={valueStyle}>{fixedBooking.customerName}</div>
          </div>
        </div>

        {/* Phone */}
        {fixedBooking.customerPhone && (
          <div style={infoRowStyle}>
            <div style={iconContainerStyle}>
              <Phone size={16} color={theme.colors.primary} />
            </div>
            <div style={infoTextStyle}>
              <div style={labelStyle}>Teléfono</div>
              <div style={valueStyle}>{fixedBooking.customerPhone}</div>
            </div>
          </div>
        )}

        {/* Price */}
        <div style={infoRowStyle}>
          <div style={iconContainerStyle}>
            <DollarSign size={16} color={theme.colors.primary} />
          </div>
          <div style={infoTextStyle}>
            <div style={labelStyle}>Precio Acordado</div>
            <div style={{ ...valueStyle, color: theme.colors.success }}>
              ${fixedBooking.agreedPrice.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Date Range */}
        <div style={{ ...infoRowStyle, marginBottom: 0 }}>
          <div style={iconContainerStyle}>
            <Calendar size={16} color={theme.colors.primary} />
          </div>
          <div style={infoTextStyle}>
            <div style={labelStyle}>Período</div>
            <div style={valueStyle}>
              Desde {formatDate(fixedBooking.startDate)}
              {fixedBooking.endDate && ` hasta ${formatDate(fixedBooking.endDate)}`}
            </div>
          </div>
        </div>

        {/* Notes */}
        {fixedBooking.notes && (
          <div
            style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: theme.colors.background,
              borderRadius: theme.borderRadius.input,
              fontSize: theme.fonts.sizeSmall,
              color: theme.colors.textSecondary,
            }}
          >
            {fixedBooking.notes}
          </div>
        )}

        {/* Generated bookings count */}
        {fixedBooking.generatedCount !== undefined && fixedBooking.generatedCount > 0 && (
          <div
            style={{
              marginTop: '16px',
              padding: '8px 12px',
              backgroundColor: `${BOOKING_COLORS.FIXED}15`,
              borderRadius: theme.borderRadius.input,
              fontSize: theme.fonts.sizeSmall,
              color: BOOKING_COLORS.FIXED,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span>📅</span>
            <span>
              {fixedBooking.generatedCount} reservas generadas
            </span>
          </div>
        )}
      </div>

      {fixedBooking.status !== 'CANCELLED' && (
        <div style={footerStyle}>
          {fixedBooking.status === 'ACTIVE' && onPause && (
            <button
              style={secondaryButtonStyle}
              onClick={() => onPause(fixedBooking.id)}
              disabled={loading}
            >
              <Pause size={14} />
              Pausar
            </button>
          )}

          {fixedBooking.status === 'PAUSED' && onResume && (
            <button
              style={primaryButtonStyle}
              onClick={() => onResume(fixedBooking.id)}
              disabled={loading}
            >
              <Play size={14} />
              Reanudar
            </button>
          )}

          {onEdit && (
            <button
              style={secondaryButtonStyle}
              onClick={() => onEdit(fixedBooking)}
              disabled={loading}
            >
              <Edit2 size={14} />
              Editar
            </button>
          )}

          {onCancel && (
            <button
              style={dangerButtonStyle}
              onClick={() => {
                if (window.confirm('¿Estás seguro de cancelar este turno fijo?')) {
                  onCancel(fixedBooking.id);
                }
              }}
              disabled={loading}
            >
              <Trash2 size={14} />
              Cancelar
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FixedBookingCard;
